import { ReactNode } from "react";
import { COVERSHEET_PROPS, displayDateAndTimeCompact, displayDateString, UserInfo } from "Backend";
import { Checkbox, TableCell, TableRow, Tooltip } from "@mui/material";
import SimpleMenu from "components/SimpleMenu";
import formatSizeBytes from "helpers/formatSizeBytes";
import DocumentName from "components/DocumentName";
import ProgressBar from "components/ProgressBar";
import theme from "theme";
import { TableColumns } from "./TableColumns";
import { AppRoutes } from "AppRoutes";
import IntakeStatus from "./IntakeStatus";
import { Link } from "react-router-dom";
import { CollectUserView, CustomDocumentProperty } from "generated/graphql";
import { type DocumentIntakeAugmented } from "redux/slices/case";
import IntakeUploadedBy from "routes/lenses/lens/case/intakes/IntakeUploadedBy";

interface IntakeRowProps {
  intake: DocumentIntakeAugmented;
  caseId: number;
  lensId: number;
  actionItems: ActionItem[];
  columns: TableColumns;
  selected?: boolean;
  onSelect?: (intake: DocumentIntakeAugmented) => void;
  documentProperties: CustomDocumentProperty[];
  showDocumentProperties?: boolean;
  users: UserInfo[];
  collectUsers: CollectUserView[];
}

export interface ActionItem {
  name: string;
  icon: ReactNode;
  action: () => void;
  disabled?: boolean;
}

const IntakeRow = (props: IntakeRowProps) => {
  const {
    intake,
    caseId,
    lensId,
    columns,
    selected,
    onSelect,
    documentProperties,
    actionItems,
    showDocumentProperties,
    users,
    collectUsers,
  } = props;

  return (
    <TableRow
      hover
      key={intake.id}
      selected={selected}
      sx={{
        whiteSpace: "nowrap",
      }}>
      {columns.select && (
        <TableCell sx={{ px: 0 }}>
          <Checkbox
            checked={selected}
            onChange={() => {
              if (onSelect) onSelect(intake);
            }}
            size="small"
            color="secondary"
          />
        </TableCell>
      )}
      {columns.actions && (
        <TableCell align="left">
          <SimpleMenu hamburger items={actionItems} />
        </TableCell>
      )}
      {columns.name && (
        <TableCell
          sx={{
            ...theme.mixins.linkLike,
            overflow: { xs: "hidden" },
            textOverflow: { xs: "ellipsis" },
            whiteSpace: { xs: "nowrap" },
            backgroundColor: "white",
          }}>
          <Link
            to={AppRoutes.caseDocumentsBuilder({
              lensId,
              caseId,
              documentId: intake.id,
            })}>
            {/* Larger than 32rem causes horiz scroll on the case home usage */}
            <DocumentName name={intake.name} className="max-w-80 2xl:max-w-[32rem]" />
          </Link>
        </TableCell>
      )}
      {columns.id && <TableCell>{intake.id}</TableCell>}
      {columns.uploadDate && (
        <Tooltip
          title={displayDateAndTimeCompact(intake.createdAt)}
          arrow
          PopperProps={{
            modifiers: [
              {
                name: "offset",
                options: {
                  offset: [0, -24],
                },
              },
            ],
          }}>
          <TableCell>{displayDateString(intake.createdAt)}</TableCell>
        </Tooltip>
      )}
      {columns.processedOn && (
        <Tooltip
          title={displayDateAndTimeCompact(intake.processedOn)}
          arrow
          PopperProps={{
            modifiers: [
              {
                name: "offset",
                options: {
                  offset: [0, -24],
                },
              },
            ],
          }}>
          <TableCell>{displayDateString(intake.processedOn)}</TableCell>
        </Tooltip>
      )}
      {columns.fileSize && <TableCell>{formatSizeBytes(intake.sizeBytes)}</TableCell>}
      {columns.pages && <TableCell>{intake.pages}</TableCell>}
      {columns.readQuality && (
        <TableCell>
          {intake.averageConfidence && <ProgressBar showBar={false} size="small" value={intake.averageConfidence} />}
        </TableCell>
      )}
      {columns.status && (
        <TableCell
          sx={{
            whiteSpace: "nowrap",
          }}>
          <IntakeStatus intake={intake} />
        </TableCell>
      )}
      {columns.coversheets && (
        <>
          <TableCell>{intake.properties.find((p) => p.key === COVERSHEET_PROPS.DATE)?.value}</TableCell>
          <TableCell>{intake.properties.find((p) => p.key === COVERSHEET_PROPS.APPROVED_DATE)?.value}</TableCell>
          <TableCell>{intake.properties.find((p) => p.key === COVERSHEET_PROPS.APPROVED_BY)?.value}</TableCell>
        </>
      )}
      {showDocumentProperties &&
        documentProperties.map((dp) => (
          <TableCell
            key={dp.key}
            sx={{
              whiteSpace: "nowrap",
            }}>
            {intake.properties.find((p) => p.key === dp.key)?.value}
          </TableCell>
        ))}
      {columns.uploadedBy && (
        <TableCell>
          <IntakeUploadedBy intake={intake} users={users} collectUsers={collectUsers} />
        </TableCell>
      )}
    </TableRow>
  );
};

export default IntakeRow;
